import React from "react";
import { Link, useParams } from "react-router-dom";
import { Spinner } from "react-bootstrap";
import { useRequest } from "../hooks/useRequest";
import { useAuth } from "../context/AuthContext";
import { useLeaguePublicContext } from "./LeaguePublicLayout";
import {
  getTeamUrl,
  getTeamPlayersUrl,
  getSeasonStatsUrl,
  getTeamPlayerStatsUrl,
  API_BASE_URL,
} from "../config/api";
import {
  Team,
  Player,
  TeamStat,
  Game,
  PlayerStat,
  GoalieStat,
} from "../types/models";
import { PaginatedResponse } from "../types/api";
import TeamDisplay from "../Common/TeamDisplay";
import TeamCalendarButton from "./TeamCalendarButton";
import {
  formatGameDateShort,
  formatGameDateWithWeekday,
  formatGameTime,
} from "../utils/dateUtils";

type RosterSortColumn = "jersey" | "goals" | "assists" | "points";
type SortDirection = "asc" | "desc";

export default function TeamProfilePage() {
  const { teamId, abbr } = useParams<{ teamId: string; abbr: string }>();
  const auth = useAuth();
  const { seasonId, gameTypeFilter, setPageDetail } = useLeaguePublicContext();
  const [rosterGameType, setRosterGameType] = React.useState<
    "regular" | "playoff"
  >(gameTypeFilter === "playoff" ? "playoff" : "regular");
  const [sortColumn, setSortColumn] =
    React.useState<RosterSortColumn>("jersey");
  const [sortDirection, setSortDirection] =
    React.useState<SortDirection>("asc");

  const teamIdNum = parseInt(teamId || "0");

  // Reset roster game type when season or game type filter changes
  React.useEffect(() => {
    setRosterGameType(gameTypeFilter === "playoff" ? "playoff" : "regular");
  }, [seasonId, gameTypeFilter]);

  // Handle column header click for sorting
  const handleSort = (column: RosterSortColumn) => {
    if (sortColumn === column) {
      // Toggle direction if clicking the same column
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      // Default to ascending for jersey, descending for stats
      setSortColumn(column);
      setSortDirection(column === "jersey" ? "asc" : "desc");
    }
  };

  // Fetch team details
  const { data: team } = useRequest<Team>(getTeamUrl(teamIdNum), auth.token);

  // Put the team name in the page title once it loads.
  React.useEffect(() => {
    if (team?.name) setPageDetail(team.name);
  }, [team, setPageDetail]);

  // Fetch team players (roster)
  const { data: playersResponse } = useRequest<PaginatedResponse<Player>>(
    getTeamPlayersUrl(teamIdNum),
    auth.token,
  );

  // Fetch team stats for current season
  const { data: statsResponse } = useRequest<TeamStat[]>(
    seasonId ? getSeasonStatsUrl(seasonId, rosterGameType) : undefined,
    auth.token,
  );

  // Fetch team's games for the season
  const { data: gamesResponse } = useRequest<PaginatedResponse<Game>>(
    seasonId && team
      ? `${API_BASE_URL}/seasons/${seasonId}/games?team_id=${teamIdNum}`
      : undefined,
    auth.token,
  );

  // Fetch team player stats for the season
  const { data: playerStatsResponse } = useRequest<PlayerStat[]>(
    seasonId ? getTeamPlayerStatsUrl(teamIdNum, rosterGameType) : undefined,
    auth.token,
  );

  // Fetch goalie stats for the season
  const { data: goalieStatsResponse } = useRequest<GoalieStat[]>(
    seasonId
      ? getTeamPlayerStatsUrl(teamIdNum, rosterGameType, "goalie")
      : undefined,
    auth.token,
  );

  if (!team || !seasonId) {
    return (
      <div className="lp-loading">
        <Spinner animation="border" variant="secondary" role="status" />
      </div>
    );
  }

  const players = playersResponse?.data || [];
  const teamStats = statsResponse?.find((s) => s.team_id === teamIdNum);
  const allGames = gamesResponse?.data || [];
  const playerStats = playerStatsResponse || [];

  // Create a map of player stats by player_id for quick lookup
  const statsMap = new Map(playerStats.map((stat) => [stat.player_id, stat]));

  // Split games into recent (completed) and upcoming (scheduled)
  const completedGames = allGames
    .filter((g) => g.state === "completed")
    .sort((a, b) => new Date(b.date).getTime() - new Date(a.date).getTime())
    .slice(0, 5);

  const upcomingGames = allGames
    .filter((g) => g.state === "scheduled")
    .sort((a, b) => new Date(a.date).getTime() - new Date(b.date).getTime())
    .slice(0, 5);

  // Calculate record for recent games
  const recentRecord = completedGames.reduce(
    (acc, game) => {
      const isHome = game.home_team_id === teamIdNum;
      const teamScore = isHome ? game.home_score : game.visitor_score;
      const oppScore = isHome ? game.visitor_score : game.home_score;

      if (teamScore! > oppScore!) {
        acc.wins++;
      } else if (teamScore! < oppScore!) {
        acc.losses++;
      } else {
        acc.ties++;
      }
      return acc;
    },
    { wins: 0, losses: 0, ties: 0 },
  );

  // Sort players based on selected column and direction
  const sortedPlayers = [...players].sort((a, b) => {
    let valA: number;
    let valB: number;

    if (sortColumn === "jersey") {
      valA = parseInt(a.jersey || "999");
      valB = parseInt(b.jersey || "999");
    } else {
      // Get stats for each player
      const statsA = statsMap.get(a.id);
      const statsB = statsMap.get(b.id);

      // Handle stat columns
      if (sortColumn === "goals") {
        valA = statsA?.goals ?? -1;
        valB = statsB?.goals ?? -1;
      } else if (sortColumn === "assists") {
        valA = statsA?.assists ?? -1;
        valB = statsB?.assists ?? -1;
      } else {
        // points
        valA = statsA?.points ?? -1;
        valB = statsB?.points ?? -1;
      }
    }

    const diff = sortDirection === "asc" ? valA - valB : valB - valA;

    // Secondary sort: if values are equal and not sorting by jersey, sort by points (desc)
    if (diff === 0 && sortColumn !== "jersey" && sortColumn !== "points") {
      const statsA = statsMap.get(a.id);
      const statsB = statsMap.get(b.id);
      return (statsB?.points ?? -1) - (statsA?.points ?? -1);
    }

    return diff;
  });

  // Season totals shown alongside the team name in the page header
  const headerStats = teamStats
    ? [
        { label: "GP", title: "Games played", value: teamStats.games_played },
        { label: "W", title: "Wins", value: teamStats.wins },
        { label: "L", title: "Losses", value: teamStats.losses },
        { label: "T", title: "Ties", value: teamStats.ties },
        { label: "PTS", title: "Points", value: teamStats.points },
      ]
    : [];

  return (
    <div>
      {/* Team Header */}
      <div className="lp-card mb-3">
        <div className="lp-team-header">
          <div className="lp-team-header-title">
            <h2 className="lp-team-header-name">
              <TeamDisplay team={team} />
            </h2>
            <TeamCalendarButton team={team} />
          </div>
          {headerStats.length > 0 && (
            <div className="lp-team-header-stats">
              {headerStats.map(({ label, title, value }) => (
                <div key={label} className="lp-team-stat" title={title}>
                  <span className="lp-team-stat-value">{value}</span>
                  <span className="lp-team-stat-label">{label}</span>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>

      {/* Roster */}
      <div className="lp-card mb-3">
        <div className="lp-roster-header">
          <strong style={{ fontSize: "13px" }}>Roster</strong>
          <div
            className="lp-game-type-filter"
            role="radiogroup"
            aria-label="Filter by game type"
          >
            <button
              type="button"
              role="radio"
              aria-checked={rosterGameType === "regular"}
              className={`lp-filter-btn${
                rosterGameType === "regular" ? " active" : ""
              }`}
              onClick={() => setRosterGameType("regular")}
            >
              Regular Season
            </button>
            <button
              type="button"
              role="radio"
              aria-checked={rosterGameType === "playoff"}
              className={`lp-filter-btn${
                rosterGameType === "playoff" ? " active" : ""
              }`}
              onClick={() => setRosterGameType("playoff")}
            >
              Playoffs
            </button>
          </div>
        </div>
        <div>
          {sortedPlayers.length === 0 ? (
            <div className="lp-card-empty">No players on roster</div>
          ) : (
            <table className="lp-table">
              <thead>
                <tr>
                  <th
                    className="lp-th-left"
                    style={{
                      width: "60px",
                      cursor: "pointer",
                      userSelect: "none",
                    }}
                    onClick={() => handleSort("jersey")}
                  >
                    #{" "}
                    {sortColumn === "jersey" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th className="lp-th-left">Player</th>
                  <th>Position</th>
                  <th
                    title="Goals"
                    onClick={() => handleSort("goals")}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    G{" "}
                    {sortColumn === "goals" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    title="Assists"
                    onClick={() => handleSort("assists")}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    A{" "}
                    {sortColumn === "assists" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                  <th
                    title="Points"
                    onClick={() => handleSort("points")}
                    style={{ cursor: "pointer", userSelect: "none" }}
                  >
                    PTS{" "}
                    {sortColumn === "points" &&
                      (sortDirection === "asc" ? "↑" : "↓")}
                  </th>
                </tr>
              </thead>
              <tbody>
                {sortedPlayers.map((player) => {
                  const stats = statsMap.get(player.id);
                  return (
                    <tr key={player.id}>
                      <td className="lp-td-left">
                        <strong>{player.jersey || "-"}</strong>
                      </td>
                      <td className="lp-td-left">
                        <Link
                          to={`/leagues/${abbr}/players/${player.id}`}
                          style={{
                            color: "var(--text-primary)",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                        >
                          {player.last_name}, {player.first_name}
                        </Link>
                      </td>
                      <td
                        style={{
                          color: "var(--text-secondary)",
                          fontSize: "12px",
                        }}
                      >
                        {player.position || "—"}
                      </td>
                      <td>{stats?.goals ?? "—"}</td>
                      <td>{stats?.assists ?? "—"}</td>
                      <td>{stats?.points ?? "—"}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* Goalies */}
      {goalieStatsResponse && goalieStatsResponse.length > 0 && (
        <div className="lp-card mb-3">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-medium)",
              backgroundColor: "var(--bg-secondary)",
            }}
          >
            <strong style={{ fontSize: "13px" }}>Goalies</strong>
          </div>
          <div>
            <table className="lp-table">
              <thead>
                <tr>
                  <th className="lp-th-left" style={{ width: "60px" }}>
                    #
                  </th>
                  <th className="lp-th-left">Player</th>
                  <th title="Games played">GP</th>
                  <th title="Wins">W</th>
                  <th title="Losses">L</th>
                  <th title="Ties">T</th>
                  <th title="Goals Against">GA</th>
                  <th title="Goals Against Average">GAA</th>
                  <th title="Shutouts">SO</th>
                  <th title="Goals">G</th>
                  <th title="Assists">A</th>
                  <th title="Penalty minutes">PIM</th>
                </tr>
              </thead>
              <tbody>
                {goalieStatsResponse.map((goalie) => {
                  // Find player from roster to get jersey number
                  const player = players.find((p) => p.id === goalie.player_id);
                  return (
                    <tr key={goalie.player_id}>
                      <td className="lp-td-left">
                        <strong>{player?.jersey || "-"}</strong>
                      </td>
                      <td className="lp-td-left">
                        <Link
                          to={`/leagues/${abbr}/players/${goalie.player_id}`}
                          style={{
                            color: "var(--text-primary)",
                            textDecoration: "none",
                            fontWeight: 500,
                          }}
                        >
                          {goalie.player_name}
                        </Link>
                      </td>
                      <td>{goalie.games_played}</td>
                      <td>{goalie.wins}</td>
                      <td>{goalie.losses}</td>
                      <td>{goalie.ties}</td>
                      <td>{goalie.goals_against}</td>
                      <td>{goalie.goals_against_average.toFixed(2)}</td>
                      <td>{goalie.shutouts}</td>
                      <td>{goalie.goals}</td>
                      <td>{goalie.assists}</td>
                      <td>{goalie.penalty_minutes}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Recent Games & Upcoming Games */}
      <div className="lp-team-games-grid">
        {/* Recent Games */}
        <div className="lp-card">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-medium)",
              backgroundColor: "var(--bg-secondary)",
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <strong style={{ fontSize: "13px" }}>Recent Games</strong>
            {completedGames.length > 0 && (
              <span
                style={{ fontSize: "12px", color: "var(--text-secondary)" }}
              >
                {recentRecord.wins}-{recentRecord.losses}-{recentRecord.ties}
              </span>
            )}
          </div>
          <div>
            {completedGames.length === 0 ? (
              <div className="lp-card-empty">No recent games</div>
            ) : (
              completedGames.map((game) => {
                const isHome = game.home_team_id === teamIdNum;
                const opponentId = isHome
                  ? game.visitor_team_id
                  : game.home_team_id;
                const opponentName = isHome
                  ? game.visitor_team_name
                  : game.home_team_name;
                const opponentColor = isHome
                  ? game.visitor_team_colour_code
                  : game.home_team_colour_code;
                const teamScore = isHome ? game.home_score : game.visitor_score;
                const oppScore = isHome ? game.visitor_score : game.home_score;
                const won = (teamScore ?? 0) > (oppScore ?? 0);

                return (
                  <Link
                    key={game.id}
                    to={`/leagues/${abbr}/games/${game.id}`}
                    style={{
                      display: "block",
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--border-subtle)",
                      textDecoration: "none",
                      color: "var(--text-primary)",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        justifyContent: "space-between",
                        alignItems: "center",
                        marginBottom: "4px",
                      }}
                    >
                      <div>
                        <span
                          style={{
                            fontSize: "12px",
                            color: "var(--text-secondary)",
                          }}
                        >
                          {isHome ? "vs" : "@"}
                        </span>{" "}
                        <span style={{ fontSize: "14px", fontWeight: 500 }}>
                          <TeamDisplay
                            team={{
                              id: opponentId,
                              name: opponentName || "Unknown Team",
                              colour_code: opponentColor,
                            }}
                          />
                        </span>
                      </div>
                      <div>
                        <span
                          style={{
                            fontSize: "16px",
                            fontWeight: 700,
                            color: won ? "var(--color-success)" : "#212529",
                          }}
                        >
                          {teamScore}
                        </span>
                        <span
                          style={{
                            margin: "0 4px",
                            color: "var(--text-muted)",
                          }}
                        >
                          -
                        </span>
                        <span
                          style={{
                            fontSize: "16px",
                            color: "var(--text-muted)",
                          }}
                        >
                          {oppScore}
                        </span>
                      </div>
                    </div>
                    <div
                      style={{ fontSize: "11px", color: "var(--text-muted)" }}
                    >
                      {formatGameDateShort(game.date)}
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>

        {/* Upcoming Games */}
        <div className="lp-card">
          <div
            style={{
              padding: "12px 16px",
              borderBottom: "1px solid var(--border-medium)",
              backgroundColor: "var(--bg-secondary)",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              gap: "8px",
            }}
          >
            <strong style={{ fontSize: "13px" }}>Upcoming Games</strong>
            {/* Subscribing is most tempting right where the fixtures are, so
                the calendar button sits here as well as in the team header. */}
            {team && <TeamCalendarButton team={team} />}
          </div>
          <div>
            {upcomingGames.length === 0 ? (
              <div className="lp-card-empty">No upcoming games</div>
            ) : (
              upcomingGames.map((game) => {
                const isHome = game.home_team_id === teamIdNum;
                const opponentId = isHome
                  ? game.visitor_team_id
                  : game.home_team_id;
                const opponentName = isHome
                  ? game.visitor_team_name
                  : game.home_team_name;
                const opponentColor = isHome
                  ? game.visitor_team_colour_code
                  : game.home_team_colour_code;

                return (
                  <Link
                    key={game.id}
                    to={`/leagues/${abbr}/games/${game.id}`}
                    style={{
                      display: "block",
                      padding: "12px 16px",
                      borderBottom: "1px solid var(--border-subtle)",
                      textDecoration: "none",
                      color: "var(--text-primary)",
                    }}
                  >
                    <div style={{ marginBottom: "4px" }}>
                      <span
                        style={{
                          fontSize: "12px",
                          color: "var(--text-secondary)",
                        }}
                      >
                        {isHome ? "vs" : "@"}
                      </span>{" "}
                      <span style={{ fontSize: "14px", fontWeight: 500 }}>
                        <TeamDisplay
                          team={{
                            id: opponentId,
                            name: opponentName || "Unknown Team",
                            colour_code: opponentColor,
                          }}
                        />
                      </span>
                    </div>
                    <div
                      style={{ fontSize: "11px", color: "var(--text-muted)" }}
                    >
                      {formatGameDateWithWeekday(game.date)} ·{" "}
                      {formatGameTime(game.date)}
                    </div>
                  </Link>
                );
              })
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
